/**
 * Created by Angela Liang on 07/20/2023.
 * Description:
 *
 * ------ maintenance history ------
 */

import { Component, OnInit, Input, OnDestroy, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { AppState, smartSelector } from '../../redux';
import { Store, select } from '@ngrx/store';
import { RefinebyConfig, RefinebyStatus, RefinebyWidgetAction, RefinebyType, RefinebySubConfig } from '../../widgets/refineby/refineby-view.model';
import { take, debounceTime, skip } from 'rxjs/operators';
import { BlastemailRefinebyService } from '../../services/blastemail-refineby.service';
import { BlastemailRefinebyConfig } from '../../pages/feeds/blastemail/blastemail.model';
import { Subscription, Subject } from 'rxjs';
import { ContextMenuConfig, ContextMenuShowAction } from '../../widgets/tam-context-menu/tam-context-menu.model';
import { DeviceDetectorService } from 'ngx-device-detector';
import { getBlastemailRefinebyDataState, getBlastemailRefinebyCategoryState, getBlastemailRefinebySelectedValueState, getBlastemailRefinebyStatusState, getBlastemailRefinebyCategoryFilterState } from '../../redux/reducers/blastemail-view.reducer';
import { BlastEmailRefinebySetCategory, BlastEmailRefinebySetCategoryStatus, BlastEmailRefinebyLoad, BlastEmailRefinebySetSelectedValue, BlastEmailRefinebySetCategoryOrder } from '../../redux/actions/blastemail-view.actions';
import { AUTO_SAVE_INTERVAL_5, LONG_TOUCH_INTERVAL } from '../../tamalelibs/constants/business.constants';
import { getCalendarListRefinebyCategoryFilterState, getCalendarListRefinebyCategoryState, getCalendarListRefinebySelectedValueState, getCalendarListRefinebyStatusState, getCalendarListRefinebyState, getCalendarListRefinebyDataState } from '../../redux/reducers/calendar-list.reducer';
import { CalendarRefinebyService } from '../../services/calendar-refineby.service';
import { Adhoc } from '../../tamalelibs/models/adhoc.model';
import { adhocTitleSelector } from '../../redux/reducers/adhoc.reducer';
import { CalendarListRefinebyLoad, CalendarListRefinebySetCategory, CalendarListRefinebySetCategoryOrder, CalendarListRefinebySetCategoryStatus, CalendarListRefinebySetSelectedValue } from '../../redux/actions/calendar.action';
import * as _lodash from 'lodash';
import { UsersActionTypes } from '../../redux/actions/users.actions';
import { StoreQuerierService } from '../../services/store-querier.service';
import { userSettingSelector } from '../../redux/reducers/users.reducer';
import { StringLiteralsPipe } from '../../pipes/translate.pipe';
import { CalendarRefinebyConfig, RefinebyConfigType, QueryInfo, CalendarRefinebyEvents, CalendarRefinebyActions } from './calendar-refineby.model';

@Component({
    // tslint:disable-next-line: component-selector
    selector: 'calendar-refineby',
    templateUrl: './calendar-refineby.component.html',
    styleUrls: ['./calendar-refineby.component.scss']
})
export class CalendarRefinebyComponent implements OnInit, OnDestroy {

    @Input()
    config: CalendarRefinebyConfig;
    @Input()
    isEntity: boolean;

    // #region public properties
    contextMenuConfig: ContextMenuConfig;
    isIPAD = false;
    reloading = true;
    updating = false;
    refinebyConfig: RefinebyConfig;
    // #endregion

    // #region private properties
    private _adhocTitle: Map<string, Adhoc>;
    private _adhocOrder: Array<Adhoc>;
    private _category: Array<string>;
    private _categoryFilter: Object;
    private _data: Map<string, Array<any>>;
    private _destroySubscriptions: Array<Subscription> = [];
    private _queryInfo: QueryInfo = new QueryInfo();
    private _reset = false;
    private _selectedValue: Map<string, Array<any>> = new Map();
    private _showAsName = false;
    private _status: Map<string, RefinebyStatus> = new Map();
    private _touchstart: number;
    private _updateDelay$: Subject<any> = new Subject();
    // #endregion

    // #region constructor
    constructor(private _store: Store<AppState>,
        private _deviceService: DeviceDetectorService,
        private _refinebyService: CalendarRefinebyService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _storeQuerier: StoreQuerierService,
        private _el: ElementRef,
    ) {
        this.refinebyConfig = new RefinebyConfig();
        this.isIPAD = this._deviceService.isMobile() || this._deviceService.isTablet();
    }
    // #endregion

    // #region public functions
    ngOnInit() {
        if (this.isEntity) {
            this.refinebyConfig.type = RefinebyConfigType.entityEvent;
        } else {
            this.refinebyConfig.type = RefinebyConfigType.allEvent;
        }

        this._initContextMenu();
        this._destroySubscriptions.push(
            this._store.pipe(
                select(this._relativeSelector(getCalendarListRefinebyDataState)),
                skip(1)
            ).subscribe(data => this._onSubscribeData(data)),

            this._store.pipe(
                select(this._relativeSelector(getCalendarListRefinebySelectedValueState)),
            ).subscribe(data => {
                if (data) {
                    this._selectedValue = data;
                }
            }),

            this._store.pipe(
                select(this._relativeSelector(getCalendarListRefinebyStatusState)),
            ).subscribe(data => {
                if (data) {
                    this._status = data;
                    this._resetStatus();
                }
            }),

            this._store.pipe(
                select(this._relativeSelector(getCalendarListRefinebyCategoryFilterState)),
            ).subscribe(data => {
                if (data) {
                    this._categoryFilter = data;
                    Object.keys(this._categoryFilter).forEach(key => {
                        this.refinebyConfig.categoryFilter[key] = this._categoryFilter[key];
                    });
                }
            }),
            this._store.pipe(
                select(adhocTitleSelector),
            ).subscribe(data => {
                this._adhocTitle = data || new Map();
                this._adhocOrder = Array.from(this._adhocTitle.values());
                this._adhocOrder.sort((a, b) => {
                    const alow = a.id.toLowerCase();
                    const blow = b.id.toLowerCase();
                    return alow === blow ? 0 : (alow > blow ? 1 : -1);
                });
            }),
            this.config.actions$.subscribe(action => this._onSubscribeActions(action)),
            this.refinebyConfig.events$.subscribe(event => this._onSubscribeEvents(event)),
            this._updateDelay$.pipe(
                debounceTime(1000)
            ).subscribe(() => this._handleUpdate())
        );

        const userSetting = this._storeQuerier.queryBySelector(userSettingSelector);
        if (userSetting && userSetting.calendarPreference) {
            this._category = userSetting.calendarPreference.configuration.category;
        }
        if (!this._category || this._category.length === 0) {
            this._category = this._refinebyService.DefaultCategory;
        }
        this._store.dispatch(new CalendarListRefinebySetCategory(
            this._category,
            this.config.prefix,
            this.config.instanceId
        ));
        this.refinebyConfig.category.push(...this._category);
        // Start generate config
        this._generateConfig();
        // End generate config

    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
        this._clearState();
    }

    onContextMenu(event) {
        this._changeDetectorRef.detectChanges();
        this.contextMenuConfig.show$.next(new ContextMenuShowAction(event, ''));
    }
    // For iPad
    touchstart() {
        this._touchstart = new Date().getTime();
    }

    // For iPad
    touchend(event) {
        if (this.isIPAD && new Date().getTime() - this._touchstart > LONG_TOUCH_INTERVAL) {
            event.clientX = event.pageX;
            event.clientY = event.pageY;
            this.onContextMenu(event);
        }
    }
    // #endregion

    // #region private functions
    private _changeShowAsName(showAsName: boolean) {
        this._showAsName = showAsName;
        this.refinebyConfig.actions$.next({
            type: RefinebyWidgetAction.SHOW_AS_NAME,
            payload: this._showAsName,
        });
    }

    private _clearState() {
        this._selectedValue.clear();
        this._categoryFilter = null;
        this._queryInfo.categoryFilter = this._categoryFilter;
        this._queryInfo.category = this._category;
        this._store.dispatch(
            new CalendarListRefinebySetSelectedValue(
                {
                    selectedValue: this._selectedValue,
                    categoryFilter: this._categoryFilter,
                },
                this.config.prefix,
                this.config.instanceId
            )
        );
    }

    private _generateConfig() {
        if (this._data && this._data.size > 0 && this._category && this._category.length > 0) {
            this.refinebyConfig.displayConfig.splice(0, this.refinebyConfig.displayConfig.length); // Clear all items
            let needUpdateStatus = false;
            const standardLength = this._refinebyService.StandardCategory.length;
            this._category.forEach((id) => {
                const subConfig: RefinebySubConfig = this._refinebyService.generateSubConfig(id, this._data, this._selectedValue, this._status, this._adhocTitle);
                if (subConfig != null) {
                    if (subConfig.type === RefinebyType.entity && subConfig.status.showAsName !== this._showAsName) {
                        subConfig.status.showAsName = this._showAsName;
                        this._status.set(subConfig.categoryId, subConfig.status);
                        needUpdateStatus = true;
                    }
                    const tempIndex = this._adhocOrder.findIndex(item => item.id === subConfig.categoryId);
                    if (tempIndex > -1) {
                        subConfig.defaultIndex = tempIndex + standardLength;
                    }
                    this.refinebyConfig.displayConfig.push(subConfig);
                }
            });
            if (needUpdateStatus) {
                this._resetStatus();
            }
            const emptyMap = new Map();
            const addConfig: Array<RefinebySubConfig> = [];
            this._adhocTitle.forEach((value, key, map) => {
                if (!this._category.includes(key)) {
                    const subConfig: RefinebySubConfig = this._refinebyService.generateSubConfig(key, emptyMap, emptyMap,
                        emptyMap, this._adhocTitle);
                    if (subConfig != null) {
                        addConfig.push(subConfig);
                    }
                }
            });
            this._refinebyService.StandardCategory.forEach(id => {
                if (!this._category.includes(id)) {
                    const subConfig: RefinebySubConfig = this._refinebyService.generateSubConfig(id, emptyMap, emptyMap, emptyMap, emptyMap);
                    if (subConfig != null) {
                        addConfig.push(subConfig);
                    }
                }
            });
            addConfig.forEach((config, index) => {
                const tempIndex = this._adhocOrder.findIndex(item => item.id === config.categoryId);
                if (tempIndex > -1) {
                    config.defaultIndex = tempIndex + standardLength;
                }
            });
            addConfig.sort((config1, config2) => {
                return config1.defaultIndex - config2.defaultIndex;
            });

            this.refinebyConfig.addConfig.splice(0, this.refinebyConfig.addConfig.length);
            this.refinebyConfig.addConfig.push(...addConfig);

            const tempArray: Array<string> = _lodash.cloneDeep(this._category);
            this.refinebyConfig.category.splice(0, this.refinebyConfig.category.length);
            this.refinebyConfig.category.push(...tempArray);
            this._category = tempArray;

            let tempObj: Object = {};
            if (this._categoryFilter != null) {
                tempObj = _lodash.cloneDeep(this._categoryFilter);
            }
            // tslint:disable-next-line: forin
            for (const key in this.refinebyConfig.categoryFilter) {
                delete this.refinebyConfig.categoryFilter[key];
            }
            if (tempObj != null) {
                Object.keys(tempObj).forEach(key => {
                    this.refinebyConfig.categoryFilter[key] = tempObj[key];
                });
                this._categoryFilter = tempObj;
            }
            const tempMap: Map<string, Array<any>> = _lodash.cloneDeep(this._selectedValue);
            this.refinebyConfig.selectedValue.clear();
            tempMap.forEach((value: Array<any>, key: string) => {
                this.refinebyConfig.selectedValue.set(key, value);
            });
            this._selectedValue = tempMap;
            this.reloading = false;
            if (this._reset) {
                const content = this._el.nativeElement.querySelector('.content');
                setTimeout(() => {
                    content.scrollTop = content.scrollHeight - content.clientHeight;
                    this._reset = false;
                }, 0);
            }
        }
    }

    private _handleUpdate() {
        this.config.events$.next({
            type: CalendarRefinebyEvents.UPDATE,
            payload: this._queryInfo
        });
    }

    private _initContextMenu() {
        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.items = [
            { id: '0', text: StringLiteralsPipe.translate('calendar.show_as_shortname'), checked: true },
            { id: '1', text: StringLiteralsPipe.translate('calendar.show_as_name') },
        ];
        this._destroySubscriptions.push(this.contextMenuConfig.events$.subscribe(event => this._onMenuSelect(event)));
    }

    private _load(queryInfo: QueryInfo) {
        this.reloading = true;
        this._queryInfo = queryInfo;
        this._queryInfo.category = this._category;
        this._queryInfo.categoryFilter = this._categoryFilter;
        this._store.dispatch(
            new CalendarListRefinebyLoad(
                this._queryInfo,
                this.config.prefix,
                this.config.instanceId
            )
        );
    }

    private _onMenuSelect(event) {
        const type = +event.payload.id;
        switch (type) {
            case 0:
                this.contextMenuConfig.items[1].checked = !this.contextMenuConfig.items[type].checked;
                this._changeShowAsName(this.contextMenuConfig.items[1].checked);
                break;
            case 1:
                this.contextMenuConfig.items[0].checked = !this.contextMenuConfig.items[type].checked;
                this._changeShowAsName(this.contextMenuConfig.items[type].checked);
                break;
            default:
                break;
        }
    }

    private _onSubscribeActions(action: any) {
        if (action.type === CalendarRefinebyActions.UPDATE) {
            this._update(action.payload);
        } else if (action.type === CalendarRefinebyActions.RELOAD) {
            // Only reset showMore
            this._status.forEach((value, key, map) => {
                value.showMore = false;
            });
            this._resetStatus();
            this._load(action.payload);
        } else if (action.type === CalendarRefinebyActions.RELOAD_DATA) {
            this._load(action.payload);
        }
    }

    private _onSubscribeData(data) {
        this._data = data;
        if (this.updating) {
            this._updateConfig();
        } else {
            this._generateConfig();
        }
        this._changeDetectorRef.detectChanges();
    }

    private _onSubscribeEvents(event) {
        const data = event.payload;
        if (event.type === RefinebyWidgetAction.SELECTED_OPTION_CHANGE) {
            this._selectedValue = data.selectedValue;
            this._categoryFilter = data.categoryFilter;
            if (this._categoryFilter != null && Object.keys(this._categoryFilter).length === 0) {
                this._categoryFilter = null;
            }
            this._queryInfo.categoryFilter = this._categoryFilter;
            this._queryInfo.category = this._category;
            this._store.dispatch(
                new CalendarListRefinebySetSelectedValue(
                    {
                        selectedValue: this._selectedValue,
                        categoryFilter: this._categoryFilter,
                    },
                    this.config.prefix,
                    this.config.instanceId
                )
            );
            this._updateDelay$.next('');
        } else if (event.type === RefinebyWidgetAction.CLEAR_ALL) {
            this._clearState();
            this.config.events$.next({
                type: CalendarRefinebyEvents.RELOAD,
                payload: this._queryInfo
            });
        } else if (event.type === RefinebyWidgetAction.STATUS_CHANGE) {
            this._status = data.status;
            this._resetStatus();
        } else if (event.type === RefinebyWidgetAction.CHANGE_CATEGORY_ORDER) {
            this._category = data.category;
            this._store.dispatch(
                new CalendarListRefinebySetCategoryOrder(
                    this._category,
                    this.config.prefix,
                    this.config.instanceId
                )
            );
            this.config.events$.next({
                type: CalendarRefinebyEvents.CHANGE_CATEGORY_ORDER,
                payload: this._category
            });
        } else if (event.type === RefinebyWidgetAction.RESET_CATEGORY) {
            this._reset = true;
            this._category = data.category;
            if (this._categoryFilter != null && Object.keys(this._categoryFilter).length === 0) {
                this._categoryFilter = null;
            }
            this._queryInfo.categoryFilter = this._categoryFilter;
            this._queryInfo.category = this._category;
            this._store.dispatch(
                new CalendarListRefinebySetCategoryOrder(
                    this._category,
                    this.config.prefix,
                    this.config.instanceId
                )
            );
            this.config.events$.next({
                type: CalendarRefinebyEvents.LOAD_REFINEBY,
                payload: this._queryInfo
            });
        }
    }

    private _relativeSelector(selector) {
        return (state) => {
            return selector(smartSelector(this.config.instanceId, 'calendarListReducer')(state));
        };
    }

    private _resetStatus(): void {
        this._store.dispatch(
            new CalendarListRefinebySetCategoryStatus(
                this._status,
                this.config.prefix,
                this.config.instanceId
            )
        );
        this.refinebyConfig.actions$.next({
            type: RefinebyWidgetAction.SET_STATUS,
            payload: this._status,
        });
    }

    private _update(queryInfo: QueryInfo) {
        this.updating = true;
        this._queryInfo = queryInfo;
        this._queryInfo.category = this._category;
        this._queryInfo.categoryFilter = this._categoryFilter;
        this._store.dispatch(
            new CalendarListRefinebyLoad(
                this._queryInfo,
                this.config.prefix,
                this.config.instanceId
            )
        );
    }

    private _updateConfig() {
        if (this._data && this._data.size > 0 && this._category && this._category.length > 0) {
            this.refinebyConfig.displayConfig.forEach(config => {
                this._refinebyService.updateSubConfig(config, this._data, this._selectedValue, this._status, this._showAsName);
            });
        }
        this.updating = false;
    }
    // #endregion

}
